import BreadCrumb from "Components/Common/BreadCrumb";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { useDebounce } from "Components/Hooks/useDebounce";
import CustomFormModal, { Field } from "Components/Modals/CustomFormModal";
import ImportItemsModal from "Components/Modals/ImportItemsModal";
import ItemsTable from "Components/Settings/Items/ItemsTable";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import {
  addItem,
  editItem,
  getCategories,
  getSubCategories,
} from "slices/thunks";
import * as Yup from "yup";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
  // createNameValidation,
} from "Components/constants/validation";

export interface ItemTableType {
  id: string;
  item: string;
  categories: string;
  subCategories: string;
  unit: string;
  price: string;
  unitCost: string;
}
interface ItemType {
  id?: number;
  category: number;
  subCategory: number | null;
  itemName: string;
  unit: string;
  price: number | null;
  unitCost: number | null;
}

const Items = () => {
  const dispatch = useAppDispatch();
  const [isAddItem, setIsCategory] = useState<boolean>(false);
  const [isEditItem, setIsEditItem] = useState<boolean>(false);
  const [isImportModal, setIsImportModal] = useState<boolean>(false);
  const { categories, isLoadingCategory } = useAppSelector(
    (stat) => stat.category,
  );
  const { subcategories, isLoadingSubCategory } = useAppSelector(
    (stat) => stat.subcategory,
  );
  const configData = useAppSelector((stat) => stat.config.configData);
  const [categorySearch, setCategorySearch] = useState("");
  const [subCategorySearch, setSubCategorySearch] = useState("");
  const debounceCategorySearch = useDebounce(categorySearch, 500);
  const debounceSubCategorySearch = useDebounce(subCategorySearch, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const validation = useFormik<ItemType>({
    enableReinitialize: true,
    initialValues: {
      category: 0,
      subCategory: null,
      itemName: "",
      unit: "",
      price: null,
      unitCost: null,
    },
    validationSchema: Yup.object({
      category: Yup.number()
        .min(1, "Category is required")
        .required("Category name is required"),
      subCategory: Yup.number()
        .nullable() // 👉 allow null
        .when("category", {
          is: (val: number) => val && val > 0,
          then: (schema) => schema.min(0, "Subcategory is optional"), // valid if null or >= 0
          otherwise: (schema) =>
            schema
              .nullable()
              .test(
                "required-if-no-category",
                "Please select a category first",
                (value, context) => {
                  // Allow null, only error if user selected a subcategory when category not selected
                  if (value === null || value === undefined) return true;
                  return false;
                },
              ),
        }),

      itemName: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("Item Name"))
        .min(
          VALIDATION_LIMITS.NAME.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Item Name",
            VALIDATION_LIMITS.NAME.MIN,
          ),
        )
        .max(
          VALIDATION_LIMITS.NAME.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Item Name",
            VALIDATION_LIMITS.NAME.MAX,
          ),
        ),
      unit: Yup.string().trim().required("Please select a unit"),
      price: Yup.number()
        .positive("Price must be greater than 0")
        .required(VALIDATION_MESSAGES.REQUIRED("price"))
        .max(
          VALIDATION_LIMITS.COST.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH("Price", VALIDATION_LIMITS.COST.MAX),
        ),
      unitCost: Yup.number()
        .min(0, "Unit cost must be 0 or greater")
        .required(VALIDATION_MESSAGES.REQUIRED("unit cost"))
        .max(
          VALIDATION_LIMITS.COST.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Unit cost",
            VALIDATION_LIMITS.COST.MAX,
          ),
        ),
    }),
    onSubmit: async (values, { resetForm }) => {
      console.log(values);

      try {
        setIsSubmitting(true);
        if (values.id) {
          await dispatch(
            editItem({
              id: values.id,
              name: values.itemName?.trim(),
              unit: values.unit?.trim(),
              price: values.price ?? 0,
              cost: values.unitCost ?? 0,
              category_id: values.category,
              subcategory_id: values.subCategory,
              type: "product",
            }),
          )?.unwrap();
        } else {
          await dispatch(
            addItem({
              name: values.itemName?.trim(),
              unit: values.unit?.trim(),
              price: values.price ?? 0,
              cost: values.unitCost ?? 0,
              category_id: values.category,
              subcategory_id: values.subCategory,
            }),
          )?.unwrap();
        }
        setIsCategory(false);
        setIsEditItem(false);
        resetForm();
      } catch (error) {
        console.error("Error managing item:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const ItemFields: Field[] = useMemo(
    () => [
      {
        name: "category",
        label: "Category",
        type: "select",
        placeholder: "Search or select a category",
        options: categories.map((cat) => ({
          label: cat.name,
          value: cat.id,
        })),
        isDisabled: isEditItem,
        onSearch: (e) => {
          setCategorySearch(e);
        },
        isLoadingOptions: isLoadingCategory,
        onChange: (value: number) => {
          validation.setFieldValue("category", value);
          // Reset subcategory when category changes
          validation.setFieldValue("subCategory", 0);
        },
      },
      {
        name: "subCategory",
        label: "Sub Category",
        type: "select",
        // placeholder:
        //   !validation.values.category || validation.values.category === 0
        //     ? "Select a category first"
        //     : "Select subcategory",
        placeholder: "Search or select a subcategory",
        options: subcategories
          .filter((cat) => cat.category.id === validation.values.category)
          .map((cat) => ({
            label: cat.name,
            value: cat.id,
          })),

        isDisabled:
          isEditItem ||
          !validation.values.category ||
          validation.values.category === 0,
        onSearch: (e) => {
          setSubCategorySearch(e);
        },
        isLoadingOptions: isLoadingSubCategory,
      },
      {
        name: "itemName",
        label: "Item Name",
        type: "text",
        placeholder: "Enter item name",
      },
      {
        name: "unit",
        label: "Unit",
        type: "select",
        placeholder: "Enter unit",
        options: configData?.measurement_units.map((unit) => ({
          label: unit.description,
          value: unit.id,
        })),
      },
      {
        name: "price",
        label: "Price per Unit",
        type: "number",
        placeholder: "e.g. 10",
        isPound: true,
      },
      {
        name: "unitCost",
        label: "Unit Cost",
        type: "number",
        placeholder: "e.g. 5",
        isPound: true,
      },
    ],
    [
      categories,
      configData?.measurement_units,
      isEditItem,
      isLoadingCategory,
      isLoadingSubCategory,
      subcategories,
      validation,
    ],
  );

  const handleItemModal = (isAdd: boolean) => {
    if (isAdd) {
      setIsCategory((prev) => !prev);
    } else {
      setIsEditItem((prev) => !prev);
    }
  };

  const handleEditItem = (item: any) => {
    setIsEditItem((prev) => !prev);
    validation.setFieldValue("id", item?.id);
    validation.setFieldValue("category", item?.category_id);
    validation.setFieldValue("subCategory", item?.subcategory_id);
    validation.setFieldValue("itemName", item?.name);
    validation.setFieldValue("unit", item?.unit);
    validation.setFieldValue("price", item?.price);
    validation.setFieldValue("unitCost", item?.cost);
  };

  const handleImportModal = () => {
    setIsImportModal((prev) => !prev);
  };

  document.title = "Items | EaziQuote";

  useEffect(() => {
    if (validation.values.category) {
      dispatch(
        getSubCategories({
          search: subCategorySearch,
          category_id: validation.values.category,
        }),
      );
    }
  }, [debounceSubCategorySearch, validation.values.category]);

  useEffect(() => {
    dispatch(getCategories({ search: categorySearch }));
  }, [debounceCategorySearch]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Items" pageTitle="Settings" url="/settings" />

        <ItemsTable
          onAddNewItem={handleItemModal?.bind(null, true)}
          onEditItem={handleEditItem}
          onImportItem={handleImportModal}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />

        <CustomFormModal
          isOpen={isAddItem || isEditItem}
          onCancel={
            isAddItem
              ? handleItemModal?.bind(null, true)
              : handleItemModal?.bind(null, false)
          }
          fields={ItemFields}
          title={isAddItem ? "Add Item" : "Edit Item"}
          submitText="Save Item"
          validation={validation}
          isLoading={isSubmitting}
        />

        <ImportItemsModal
          isOpen={isImportModal}
          onCancel={handleImportModal}
          currentPage={currentPage}
        />
      </Container>
    </div>
  );
};

export default Items;
